import React, { useState, useRef, useEffect, useCallback } from 'react'
import { parsePriceInput, priceToMillions } from '../../utils/formatters'
import { useInlineEditorBehavior } from '../../hooks/useInlineEditorBehavior'

interface InlineAskPriceEditorProps {
  /** Current price value in storage format (e.g., 37000000 for $37M) */
  value: number | null | undefined
  /** Current make offer flag value */
  makeOffer: boolean | null | undefined
  /** Field name for display/debugging purposes */
  fieldName: string
  /** Called when user saves new values */
  onSave: (newValue: number | null, newMakeOffer: boolean | null) => Promise<void>
  /** Called when user cancels editing */
  onCancel: () => void
  /** Optional placeholder text */
  placeholder?: string
}

/**
 * Inline editor for asking price fields with Make Offer toggle.
 * - Displays value in millions (e.g., "1.975" for $1.975M)
 * - Includes M.O. toggle button
 * - Auto-saves on blur or Enter key
 * - Cancels on Escape key
 */
export default function InlineAskPriceEditor({
  value,
  makeOffer,
  fieldName: _fieldName,
  onSave,
  onCancel,
  placeholder = '0.000'
}: InlineAskPriceEditorProps) {
  // Convert storage value to millions for editing
  const initialValue = priceToMillions(value)
  const [inputValue, setInputValue] = useState(
    initialValue !== null ? initialValue.toString() : ''
  )
  const [isMakeOffer, setIsMakeOffer] = useState<boolean>(makeOffer ?? false)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Focus and select input on mount
  useEffect(() => {
    if (inputRef.current && !isMakeOffer) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  const handleSave = useCallback(async () => {
    if (isSaving) return

    // Parse the input value (user enters millions, we store full number)
    const newValue = isMakeOffer ? null : parsePriceInput(inputValue)

    // Check if anything actually changed
    const oldValue = value ?? null
    const oldMakeOffer = makeOffer ?? false
    const valueChanged = newValue !== oldValue
    const makeOfferChanged = isMakeOffer !== oldMakeOffer

    if (!valueChanged && !makeOfferChanged) {
      onCancel()
      return
    }

    setIsSaving(true)
    try {
      await onSave(newValue, isMakeOffer || null)
    } catch (error) {
      // Stay in edit mode on error
      setIsSaving(false)
    }
  }, [inputValue, isMakeOffer, value, makeOffer, onSave, onCancel, isSaving])

  const { handleKeyDown, handleBlur } = useInlineEditorBehavior({
    onSave: handleSave,
    onCancel,
    containerRef,
    checkFocusWithinContainer: true,
    blurDelay: 150
  })

  return (
    <div ref={containerRef} className="absolute z-50 flex items-center gap-1 bg-white shadow-lg rounded p-1 -ml-1 -mt-1">
      <input
        ref={inputRef}
        type="text"
        value={isMakeOffer ? '' : inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={isSaving || isMakeOffer}
        placeholder={isMakeOffer ? 'M.O.' : placeholder}
        className={`
          w-24 px-2 py-1 text-sm text-center
          border border-green-500 rounded
          focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent
          ${isSaving || isMakeOffer ? 'bg-gray-100 text-gray-500' : 'bg-white'}
        `}
      />
      <button
        type="button"
        onClick={() => setIsMakeOffer(!isMakeOffer)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={isSaving}
        className={`
          px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap
          ${isMakeOffer
            ? 'bg-green-600 text-white hover:bg-green-700'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }
          ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        M.O.
      </button>
      {/* Visual indicator showing format hint */}
      <div className="absolute -bottom-4 left-1 text-[10px] text-gray-400">
        $M
      </div>
    </div>
  )
}

